// src/pages/DeleteAccountPage.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import MainNavigation from '../components/common/MainNavigation';
import SiteFooter from '../components/common/SiteFooter';
import StaticPageSeo from '../seo/StaticPageSeo';
import '../styles/LegalPage.css';

/**
 * Public account-deletion page (Google Play "delete account URL"). The apps
 * link here as lookbel.com/delete-account. It needs no sign-in and makes no
 * API call, so it works for someone who can no longer sign in, and it states
 * only what production does today: a request is recorded, it can be
 * cancelled for 30 days, and nothing is deleted until the completion process
 * is in place. Keep it in step with mobile AccountDeletionScreen.
 */
export const PRIVACY_EMAIL = 'privacy@lookbel.com';

const MAIL_SUBJECT = 'Account deletion request';
const MAIL_BODY =
  'Please delete my LookBel account.\n\nName on the account:\nI use LookBel as a (client / stylist):\n';

export const DELETION_MAILTO = `mailto:${PRIVACY_EMAIL}?subject=${encodeURIComponent(
  MAIL_SUBJECT
)}&body=${encodeURIComponent(MAIL_BODY)}`;

export const INTERIM_COMPLETION_TEXT =
  'We are finishing the process we use to complete account deletions. Until it is in place, your request stays on file and your account is not deleted.';

export const ON_FILE_TEXT =
  'You can still sign in and use your account while the request is on file. If you are a stylist, your profile may be hidden from public search and listings during that time.';

export const UPCOMING_BOOKINGS_TEXT =
  'Upcoming bookings must be cancelled or completed before a deletion request can be recorded.';

export const EMAIL_CANCELLATION_TEXT =
  'Emailing us asks us to cancel your request. We handle that by hand, so the request stays recorded until we have processed it. Until then, you can still cancel it yourself, straight away, in the app or on the website.';

export const EMAIL_REQUEST_TEXT =
  'You do not need the app, or to be able to sign in, to ask by email.';

export const SELF_CANCELLATION_TEXT =
  'That takes effect straight away and clears the request.';

export const MEDIA_DELETION_TEXT =
  'Photos and files that are stored for you are deleted too. Some older photos are kept in a storage system we cannot yet delete from with this process. We will not treat your deletion as complete while any of those remain.';

export const HOLD_REASONS = [
  'upcoming or in-progress bookings',
  'unsettled payments or refunds',
  'a payout in transit',
  'a wallet balance',
  'an open payment dispute',
  'an active subscription',
  'ownership of a salon on LookBel',
];

const DeleteAccountPage: React.FC = () => (
  <div className="legal-page">
    <StaticPageSeo path="/delete-account" />
    <MainNavigation variant="solid" />

    <main className="legal-page__main" id="main-content">
      <div className="legal-page__container legal-page__content">
        <header className="legal-page__header">
          <h1 className="legal-page__title">Delete your LookBel account</h1>
          <p className="legal-page__subtitle">
            How to ask LookBel LLC to delete your LookBel account and the data linked to it.
            This applies to the LookBel apps for Android and iOS and to lookbel.com.
          </p>
        </header>

        <section className="legal-section" aria-labelledby="how-to-request">
          <h2 id="how-to-request">How to request deletion</h2>
          <h3>In the LookBel app (Android and iOS)</h3>
          <p>
            Open <strong>Profile</strong>, then <strong>Password &amp; Security</strong>, then{' '}
            <strong>Delete Account</strong>. Enter your password and tap{' '}
            <strong>Request Account Deletion</strong>.
          </p>
          <h3>On the website</h3>
          <p>
            Sign in at lookbel.com, open <strong>Profile</strong>, then{' '}
            <strong>Password &amp; Security</strong>. Enter your password and select{' '}
            <strong>Request Account Deletion</strong>.
          </p>
          <h3>By email</h3>
          <p>
            {EMAIL_REQUEST_TEXT} Email <a href={DELETION_MAILTO}>{PRIVACY_EMAIL}</a> from the email
            address on your account, with the subject &ldquo;{MAIL_SUBJECT}&rdquo;. We may reply to
            that address to confirm the request before we record it. We will never ask for your
            password.
          </p>
        </section>

        <section className="legal-section" aria-labelledby="what-happens">
          <h2 id="what-happens">What happens after you ask</h2>
          <ul>
            <li>
              We record your deletion request. This does not delete your account straight away.{' '}
              {ON_FILE_TEXT}
            </li>
            <li>
              You can cancel the request for 30 days in the app or on the website, on the screen
              where you request deletion, with <strong>Cancel Account Deletion</strong>.{' '}
              {SELF_CANCELLATION_TEXT}
            </li>
            <li>
              You can also email {PRIVACY_EMAIL} to cancel. {EMAIL_CANCELLATION_TEXT}
            </li>
            <li>{INTERIM_COMPLETION_TEXT}</li>
            <li>{UPCOMING_BOOKINGS_TEXT}</li>
            <li>
              Any of these puts a deletion on hold until it is resolved: {HOLD_REASONS.join(', ')}.
            </li>
          </ul>
        </section>

        <section className="legal-section" aria-labelledby="what-is-deleted">
          <h2 id="what-is-deleted">What is deleted</h2>
          <p>
            When a deletion is completed, your profile, the messages you sent, and reviews by or
            about you are deleted. {MEDIA_DELETION_TEXT}
          </p>
        </section>

        <section className="legal-section" aria-labelledby="what-is-kept">
          <h2 id="what-is-kept">What is kept, and why</h2>
          <ul>
            <li>
              Payment, payout, and tax records are kept for as long as tax and accounting law
              requires. Our retention periods are being finalized.
            </li>
            <li>
              We may keep other information where the law requires it, or to resolve disputes
              and prevent fraud, as our <Link to="/privacy">Privacy Policy</Link> describes.
            </li>
            <li>Your wallet balance is never forfeited.</li>
          </ul>
        </section>

        <section className="legal-section" aria-labelledby="partial">
          <h2 id="partial">Deleting some data without deleting your account</h2>
          <p>
            You can edit your profile and remove your photos in the app at any time. To ask us to
            delete other specific data while keeping your account, email{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
          </p>
        </section>

        <section className="legal-section" aria-labelledby="questions">
          <h2 id="questions">Questions</h2>
          <p>
            Email <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>, or see our{' '}
            <Link to="/privacy">Privacy Policy</Link> and <Link to="/contact">Contact</Link> page.
          </p>
        </section>
      </div>
    </main>

    <SiteFooter />
  </div>
);

export default DeleteAccountPage;
